import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { MasterService } from '../../../master/services/master.service';
import { InvestmentInCompaniesService } from '../../../../modules/investment/incompanies/investment-incompanies.service';
import { PolizaCreditoService } from '../../../../services/finanzas/poliza-credito/poliza-credito.service';

export interface InvestmentCompanyOption {
  id: any;
  display: string;
}

export interface InvestmentCompanyAccountOption extends InvestmentCompanyOption {
  number: string;
  name: string;
  accountingModelsId: number;
}

export type InvestmentCompanyPayKind = 'bankAccounts' | 'paymentCards' | 'cashRegisters' | 'partners' | 'creditPolicies';

const CREDIT_CARD_TYPE = 1;

@Injectable({ providedIn: 'root' })
export class InvestmentCompanyLookupsFacade {

  private readonly payCache = new Map<InvestmentCompanyPayKind, Observable<InvestmentCompanyOption[]>>();

  constructor(
    private masterService: MasterService,
    private inCompaniesService: InvestmentInCompaniesService,
    private polizaCreditoService: PolizaCreditoService,
  ) { }

  accounts(): Promise<InvestmentCompanyAccountOption[]> {
    return this.inCompaniesService.accounts().then(list => list
      .filter(account => !!account && !!account.number)
      .map(account => ({
        id: account.number,
        display: [account.number, account.name].filter(Boolean).join(' · '),
        number: account.number,
        name: account.name || '',
        accountingModelsId: Number(account.id) || 0,
      })));
  }

  payOptions(kind: InvestmentCompanyPayKind): Promise<InvestmentCompanyOption[]> {
    let entry = this.payCache.get(kind);
    if (!entry) {
      entry = this.requestPayOptions(kind).pipe(
        catchError(error => {
          this.payCache.delete(kind);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.payCache.set(kind, entry);
    }
    return firstValueFrom(entry.pipe(map(list => list.slice())));
  }

  private requestPayOptions(kind: InvestmentCompanyPayKind): Observable<InvestmentCompanyOption[]> {
    switch (kind) {
      case 'bankAccounts':
        return this.masterService.getBankAccounts().pipe(map(list => (list || []).map((account: any) => {
          const bank = account.metadata && account.metadata.bank ? String(account.metadata.bank).trim() : '';
          return { id: account.id, display: bank ? `${bank} · ${account.display}` : account.display };
        })));
      case 'paymentCards':
        return this.masterService.getPaymentCards(CREDIT_CARD_TYPE).pipe(map(list => (list || []).map((card: any) => ({ id: card.id, display: card.display }))));
      case 'cashRegisters':
        return this.masterService.getCashRegisters().pipe(map(list => (list || [])
          .filter((item: any) => item.metadata && item.metadata.id)
          .map((item: any) => ({ id: item.metadata.id, display: item.display }))));
      case 'partners':
        return this.masterService.getParnerts().pipe(map(list => (list || []).map((partner: any) => ({ id: Number(partner.id), display: partner.display }))));
      default:
        return from(this.polizaCreditoService.listPayments()).pipe(
          map((list: any[]) => (list || []).map(policy => ({ id: policy.uid, display: [policy.banco, policy.iban].filter(Boolean).join(' · ') || 'Póliza de crédito' }))),
        );
    }
  }
}
